import {apolloClient as client} from '../../../apolloClient';
import {envIsPublicCloud} from '../../../config';
import {OrganizationStorageInfoQuery} from '../../../generated/graphql';
import {ORGANIZATION_STORAGE_INFO} from '../../../graphql/organizations';
import {Viewer} from '../../../state/viewer/types';
import {getPrimarySub, getStorageSub} from '../../../util/pricing';
import {freeStorage} from '../../../util/storage';
import {ViewerOrg} from '..';
import {
  ReachingResourceLimitWarningBannerProps,
  ResourceSubscriptionHasBeenUpgradedBannerProps,
  ResourceSubscriptionLimitExceededWarningBannerProps,
} from '.';

export type StorageBannerFlags = {
  reachingStorageLimitWarningBannerProps?: ReachingResourceLimitWarningBannerProps;
  reachingArtifactStorageLimitWarningBannerProps?: ReachingResourceLimitWarningBannerProps;
  storageSubscriptionLimitExceededWarningBannerProps?: ResourceSubscriptionLimitExceededWarningBannerProps;
  artifactStorageSubscriptionLimitExceededWarningBannerProps?: ResourceSubscriptionLimitExceededWarningBannerProps;
  storageSubscriptionHasBeenUpgradedBannerProps?: ResourceSubscriptionHasBeenUpgradedBannerProps;
  artifactStorageSubscriptionHasBeenUpgradedBannerProps?: ResourceSubscriptionHasBeenUpgradedBannerProps;
};

export async function getStorageBannerFlags(
  viewer?: Viewer,
  org?: ViewerOrg
): Promise<StorageBannerFlags> {
  const flags: StorageBannerFlags = {};

  if (!envIsPublicCloud) {
    return flags;
  }

  const primarySubscription = getPrimarySub(org);

  if (primarySubscription?.plan.name.toLowerCase() === 'enterprise') {
    return flags;
  }

  if (org == null || viewer == null) {
    return flags;
  }

  const isViewerOrgBillingAdmin =
    org.billingUser?.username === viewer?.username;

  const storageSubscription = getStorageSub(org);

  let data;

  try {
    const response = await client.query<OrganizationStorageInfoQuery>({
      query: ORGANIZATION_STORAGE_INFO,
      fetchPolicy: 'network-only',
      variables: {
        organizationID: org.id,
      },
    });

    data = response.data;
  } catch (error) {
    console.error('There was an error fetching the data: ', error);
    data = undefined;
  }

  const teams = data?.organization?.teams ?? [];

  const orgStorageBytes = teams.reduce(
    (acc, team) => acc + team.storageBytes,
    0
  );

  // 85% of bytes used banners
  if (
    orgStorageBytes != null &&
    freeStorage > orgStorageBytes &&
    orgStorageBytes >= freeStorage * 0.85
  ) {
    flags.reachingStorageLimitWarningBannerProps = {
      organizationId: org.id,
      organizationName: org.name,
      resource: 'storage',
      isBillingAdmin: isViewerOrgBillingAdmin,
      billingAdminEmail: org.billingUser?.email ?? undefined,
      hasStorageSubscription: storageSubscription != null,
    };
  }

  // 100% of bytes used with no subscription banners
  if (
    storageSubscription == null &&
    orgStorageBytes != null &&
    orgStorageBytes >= freeStorage
  ) {
    flags.storageSubscriptionLimitExceededWarningBannerProps = {
      organizationId: org.id,
      organizationName: org.name,
      isBillingAdmin: isViewerOrgBillingAdmin,
      billingAdminEmail: org.billingUser?.email ?? undefined,
      resource: 'storage',
    };
  }

  // 100% of bytes used with existing subscription banners
  if (
    storageSubscription != null &&
    orgStorageBytes != null &&
    orgStorageBytes >= freeStorage
  ) {
    flags.storageSubscriptionHasBeenUpgradedBannerProps = {
      organizationId: org.id,
      organizationName: org.name,
      resource: 'storage',
    };
  }

  return flags;
}
